import { useAuth } from '@/contexts/AuthContext';
import { fetchLatestSupportConversation, type SupportConversation } from '@/lib/supportChat';
import { useCallback, useEffect, useState } from 'react';

interface UseSupportConversationOptions {
  enabled?: boolean;
}

export function useSupportConversation(options: UseSupportConversationOptions = {}) {
  const { enabled = true } = options;
  const { user } = useAuth();
  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [loading, setLoading] = useState(Boolean(enabled && user));
  const [error, setError] = useState<Error | null>(null);

  const refreshConversation = useCallback(async (): Promise<SupportConversation | null> => {

    if (!enabled || !user) {
      setConversation(null);
      setLoading(false);
      setError(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const nextConversation = await fetchLatestSupportConversation(user.id);
      setConversation(nextConversation);
      return nextConversation;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error('Failed to load support conversation.'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled, user]);

  useEffect(() => {
    void refreshConversation();
  }, [refreshConversation]);

  return {
    conversation,
    loading,
    error,
    refreshConversation,
    setConversation,
  };
}
